import { useMemo } from 'react'
import { v4 as uuid } from 'uuid'
import { useQuery } from '@tanstack/react-query'
import { prependHttp } from '../../../../helpers/url'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { IconChoices } from '../../../common/Icon'
import { LinkSection } from '../LinkSection'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../../helpers/society'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { widgetQueries } from '../../../../api/society-widgets'
import { binderQueries } from '../../../../api/binder'
import { i18nSocietyType } from '../../../../i18n'
import { notificationsQueries } from '../../../../api/notifications'
import { useFeatures } from '../../../../hooks/useFeatures'

export const useResidentLinks = ({
  society,
  societyLockedForUser,
}: {
  society?: ISocietiesModel
  societyLockedForUser: boolean
}): LinkSection[] => {
  const { translate } = useAppTranslation()
  const authUserId = useAuthenticatedUserId()
  const id = society?._id
  const { data: widgets } = useQuery(
    widgetQueries.getWidgetsPerSociety(society?._id)
  )

  const { data: hasBinders } = useQuery(
    binderQueries.userHasBinders(society?._id || '')
  )

  const showBinders = hasBinders && hasBinders.length > 0

  const hasReportProblemEmail =
    society?.reportProblemEmailCC && society.reportProblemEmailCC?.length > 0

  const widgetIsHidden = (widgetType: string): boolean => {
    if (!society) return false
    if (!society.hiddenSocietyWidgets) {
      return false
    }
    return society.hiddenSocietyWidgets?.includes(widgetType)
  }

  const isAdmin = isUserAdminInSociety(society, authUserId as string)

  const currentUserIsAdmin = isUserAdminInSociety(society, authUserId as string)
  const currentUserIsBoard = isUserBoardMemberInSociety(
    society,
    authUserId as string
  )

  const { data: notifications } = useQuery(
    notificationsQueries.getUserUnseenUnreadCount(authUserId)
  )
  const unreadTickets = useMemo(() => {
    if (!society) return false
    const tickets = notifications?.tickets
    if (!tickets) return false
    return !!tickets?.[society?._id]
  }, [notifications?.tickets, society])
  const { enabledFeatures } = useFeatures()
  const ticketHidden =
    widgetIsHidden('ticket-question') && widgetIsHidden('ticket-report')
  const hideTicket = useMemo(() => {
    if (!enabledFeatures.ticket) {
      return true
    }
    if (ticketHidden) {
      return true
    }
    return false
  }, [enabledFeatures.ticket, ticketHidden])

  const residentLinks: LinkSection[] = [
    {
      id: uuid(),
      ...((currentUserIsAdmin || currentUserIsBoard) && {
        title: translate(
          'societySideBar.sections.resident.residentFunctionsTitle',
          { context: i18nSocietyType(society?.societyTypeDisplayName) }
        ),
      }),
      alwaysOpen: !currentUserIsAdmin && !currentUserIsBoard && true,
      sublinks: [
        ...[
          hideTicket
            ? undefined
            : {
                title: translate('ticketView.title'),
                icon: IconChoices.ASSIGNMENT,
                url: reverseUrl('society:ticket-create', { id }),
                disabled: societyLockedForUser,
                displayNotificationBadge: unreadTickets,
              },
        ],
        ...[
          !showBinders || widgetIsHidden('binder')
            ? undefined
            : {
                title: translate('binderView.title'),
                icon: IconChoices.BOOK,
                url: reverseUrl('society:binder', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('resident-list')
            ? undefined
            : {
                title: translate('residentListView.title'),
                icon: IconChoices.RESIDENT_LIST,
                url: reverseUrl('society:resident-list', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('board')
            ? undefined
            : {
                title: translate('boardListView.title', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                }),
                icon: IconChoices.GROUPS,
                url: reverseUrl('society:board', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('contacts')
            ? undefined
            : {
                title: translate('contactsView.title'),
                icon: IconChoices.GROUP,
                url: reverseUrl('society:contacts', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('calendar')
            ? undefined
            : {
                title: translate('societyCalendarListView.title'),
                icon: IconChoices.CALENDAR,
                url: reverseUrl('society:calendar', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('documents')
            ? undefined
            : {
                title: translate('documentsView.title', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                }),
                icon: IconChoices.FOLDER,
                url: reverseUrl('society:documents', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('facilities')
            ? undefined
            : {
                title: translate('facilitiesView.title'),
                icon: IconChoices.LAUNDRY,
                url: reverseUrl('society:facilities', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('faqs')
            ? undefined
            : {
                title: translate('questionsAndAnswersView.title'),
                icon: IconChoices.QUESTION_MARK,
                url: reverseUrl('society:faq', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('rules')
            ? undefined
            : {
                title: translate('houseRulesView.title'),
                icon: IconChoices.LIST,
                url: reverseUrl('society:house-rules', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('local-offers')
            ? undefined
            : {
                title: translate('localOffersView.title'),
                icon: IconChoices.LOCAL_OFFER,
                url: reverseUrl('society:local-offers', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('reportProblem') ||
          (!hasReportProblemEmail && !isAdmin)
            ? undefined
            : {
                title: translate('reportProblemView.title'),
                icon: IconChoices.REPORT,
                url: reverseUrl('society:report-problem', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('marketplace')
            ? undefined
            : {
                title: translate('marketplaceListView.title'),
                icon: IconChoices.STORE,
                url: reverseUrl('society:marketplace', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...[
          widgetIsHidden('find-friends')
            ? undefined
            : {
                title: translate('findFriendsView.title'),
                icon: IconChoices.GROUP,
                url: reverseUrl('society:find-friends', { id }),
                disabled: societyLockedForUser,
              },
        ],
        ...(widgets
          ? widgets.map((widget) => ({
              icon:
                widget.type === 'link-widget'
                  ? IconChoices.EXTERNAL_LINK_2
                  : IconChoices.DOCUMENT,
              widgetType: widget.type,
              title: widget.title,
              url:
                widget.type === 'link-widget'
                  ? prependHttp(widget.link || '')
                  : reverseUrl('society:widgets-detail', {
                      id,
                      widgetId: widget._id,
                    }),
              urlIsExternal: widget.type === 'link-widget',
              widgetId: uuid(),
              disabled: societyLockedForUser,
            }))
          : []),
      ].filter((item) => item !== undefined),
    },
  ]

  return residentLinks
}
